import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function ResetPassword() {
  const { token } = useParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token, new_password: password
      });
      setSuccess(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F4] flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-sm border border-[#E5E4E0] p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-[#2A2C2A] tracking-tight">Reset Password</h1>
            <p className="text-sm text-[#5C5F5C] mt-1">Enter your new password below</p>
          </div>

          {success ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <CheckCircle className="w-12 h-12 text-[#4B6651] mx-auto" />
              <h2 className="text-lg font-semibold text-[#2A2C2A]">Password Reset</h2>
              <p className="text-sm text-[#5C5F5C]">Your password has been updated. You can now log in with your new password.</p>
              <Link to="/login">
                <Button className="bg-[#4B6651] hover:bg-[#3E5543] text-white mt-2" data-testid="go-to-login-btn">
                  Go to Login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#2A2C2A] text-sm font-medium">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F5C]" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="At least 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 border-[#E5E4E0] bg-white h-11"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C5F5C] hover:text-[#2A2C2A]"
                    data-testid="reset-toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#2A2C2A] text-sm font-medium">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F5C]" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Repeat your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 border-[#E5E4E0] bg-white h-11"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#4B6651] hover:bg-[#3E5543] text-white h-11"
                data-testid="reset-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Reset Password
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
